import React, { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'

/**
 * FeedbackModal — modal reusável com 3 variantes:
 *   - 'error'   → X grande vermelho. Use pra "info não encontrada".
 *   - 'warning' → ⚠ amarelo. Use pra "já feito / sem vínculo / regra de negócio".
 *   - 'success' → ✓ verde grande. Use pra confirmação visual de sucesso (raro,
 *                                 já tem feedback inline na maior parte das telas).
 *
 * Props:
 *   visible (bool)       — controla a visibilidade
 *   kind ('error'|'warning'|'success')
 *   title (string)
 *   message (string|node)
 *   onClose (fn)         — chamado ao clicar Fechar OU em ESC OU click no backdrop
 *   children (node)      — opcional, conteúdo extra (ex: caixa do "box correto")
 *   autoCloseMs (number) — opcional, fecha sozinho após N ms
 */
export default function FeedbackModal({
  visible, kind = 'error', title, message, onClose, children, autoCloseMs,
}) {
  // ESC fecha
  useEffect(() => {
    if (!visible) return
    function onKey(e) { if (e.key === 'Escape') onClose && onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [visible, onClose])

  // Auto-close opcional
  useEffect(() => {
    if (!visible || !autoCloseMs) return
    const t = setTimeout(() => onClose && onClose(), autoCloseMs)
    return () => clearTimeout(t)
  }, [visible, autoCloseMs, onClose])

  const icon = kind === 'error'   ? '✕'
             : kind === 'warning' ? '⚠'
             : '✓'
  const defaultTitle = title
    || (kind === 'error'   ? 'Erro'
       : kind === 'warning' ? 'Atenção'
       : 'Sucesso')

  return (
    <Overlay $visible={visible} onClick={onClose}>
      <Box $kind={kind} onClick={e => e.stopPropagation()}>
        <Icon $kind={kind}>{icon}</Icon>
        <Title $kind={kind}>{defaultTitle}</Title>
        {message && <Message>{message}</Message>}
        {children}
        <Actions>
          <Btn $kind={kind} type="button" onClick={onClose} autoFocus>
            Fechar
          </Btn>
        </Actions>
      </Box>
    </Overlay>
  )
}

// ─── STYLES ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
`

const c = {
  bg2:    '#111318',
  bdr2:   '#2a3040',
  txt:    '#e8ecf0',
  txt2:   '#8a95a3',
  txt3:   '#5a6370',
  acc:    '#00e5a0',
  warn:   '#f59e0b',
  danger: '#ef4444',
  mono:   "'Space Mono',monospace",
  sans:   "'DM Sans',sans-serif",
}

const colorFor = (kind) =>
  kind === 'warning' ? c.warn
  : kind === 'success' ? c.acc
  : c.danger

const bgFor = (kind) =>
  kind === 'warning' ? 'rgba(245,158,11,.15)'
  : kind === 'success' ? 'rgba(0,229,160,.15)'
  : 'rgba(239,68,68,.15)'

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 3000;
  background: rgba(0,0,0,.7);
  backdrop-filter: blur(3px);
  display: ${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items: center; justify-content: center;
  padding: 20px;
`

const Box = styled.div`
  background: ${c.bg2};
  border: 1px solid ${({ $kind }) => colorFor($kind)};
  border-top: 6px solid ${({ $kind }) => colorFor($kind)};
  border-radius: 14px;
  padding: 30px 32px;
  width: 480px;
  max-width: calc(100vw - 20px);
  text-align: center;
  box-shadow: 0 20px 60px ${({ $kind }) => `${colorFor($kind)}33`};
  animation: ${fadeIn} .18s ease;
  font-family: ${c.sans};
`

const Icon = styled.div`
  width: 84px; height: 84px;
  border-radius: 50%;
  background: ${({ $kind }) => bgFor($kind)};
  border: 3px solid ${({ $kind }) => colorFor($kind)};
  color: ${({ $kind }) => colorFor($kind)};
  font-size: 48px; font-weight: 700; line-height: 1;
  display: inline-flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
`

const Title = styled.h2`
  font-size: 22px; font-weight: 700;
  color: ${({ $kind }) => colorFor($kind)};
  margin-bottom: 10px;
`

const Message = styled.div`
  font-size: 14px; color: ${c.txt}; line-height: 1.5;
  margin-bottom: 18px;
  > strong { color: ${c.txt}; font-weight: 700; }
`

const Actions = styled.div`
  display: flex; justify-content: center; gap: 8px; margin-top: 4px;
`

const Btn = styled.button`
  padding: 12px 28px;
  background: ${({ $kind }) => colorFor($kind)};
  border: 1px solid ${({ $kind }) => colorFor($kind)};
  color: ${({ $kind }) => $kind === 'warning' ? '#000' : ($kind === 'success' ? '#000' : '#fff')};
  border-radius: 8px;
  font-size: 14px; font-weight: 700; font-family: ${c.sans};
  cursor: pointer; transition: .12s;
  &:hover { filter: brightness(1.1); }
  &:focus { outline: 2px solid ${({ $kind }) => colorFor($kind)}66; outline-offset: 2px; }
`

// Exporta também os subcomponentes pra quem quiser compor (ex: blocos extras)
export const FeedbackExtra = styled.div`
  background: rgba(0,229,160,.10);
  border: 1px solid ${c.acc};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
  text-align: left;
  font-size: 13px; color: ${c.txt2};
  .lbl { font-size: 10px; color: ${c.txt3}; text-transform: uppercase; letter-spacing: .05em; }
  .val { font-family: ${c.mono}; font-size: 15px; font-weight: 700; color: ${c.txt}; margin-top: 3px; }
  .meta { font-size: 11px; color: ${c.txt3}; margin-top: 3px; }
`
